// src/admin/pages/QueueAdmin.jsx
import { useEffect, useMemo, useRef, useState } from 'react';

import { db } from '../../firebase/firebase';
import {
  doc, onSnapshot, collection, query, where, orderBy, limit,
  updateDoc, serverTimestamp, deleteField,runTransaction
} from 'firebase/firestore';

import { startInhouseReception, 
        endInhouseReception ,
        startOnlineReception ,
        endOnlineReception,allocateNextSlot ,
        sendReservationMessageBySlot
} from '../../firebase/callFunctions';
import "../styles/admin.css";


const BEEP_PATH = '/sounds/reserve-beep.mp3';

const useBeep = () => {
  const audioRef = useRef(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('qa_soundOn') === '1');
  const [soundLocked, setSoundLocked] = useState(false); // 自動再生ブロック時に表示

  useEffect(() => {
    const a = new Audio(BEEP_PATH);
    a.preload = 'auto';
    a.volume = 0.6;
    audioRef.current = a;
  }, []);

  const playBeep = async () => {
    if (!soundOn || !audioRef.current) return;
    try {
      audioRef.current.currentTime = 0;
      await audioRef.current.play();
      setSoundLocked(false);
    } catch {
      // 自動再生ブロック
      setSoundLocked(true);
    }
  };

  const toggleSound = (on) => {
    setSoundOn(on);
    localStorage.setItem('qa_soundOn', on ? '1' : '0');
  };

  // const unlockSound = async () => {
  //   if (!audioRef.current) return;
  //   try {
  //     await audioRef.current.play();
  //     audioRef.current.pause();
  //     audioRef.current.currentTime = 0;
  //     setSoundLocked(false);
  //     toggleSound(true);
  //   } catch {
  //     setSoundLocked(true);
  //   }
  // };

  const unlockSound = async () => {
  if (!audioRef.current) return;
  try {
    audioRef.current.currentTime = 0;
    await audioRef.current.play();   // タップ時に実際に鳴らす
    setSoundLocked(false);
    toggleSound(true);
  } catch {
    setSoundLocked(true);
  }
};


  return { playBeep, soundOn, toggleSound, soundLocked, unlockSound };
};




const fmtTime = (d = new Date()) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;

// Timestamp/Date/number(ミリ秒) → HH:MM に
const toHM = (v) => {
  if (!v) return '';
  const d = v?.toDate?.() ?? (v instanceof Date ? v : new Date(v));
  if (Number.isNaN(d?.getTime?.())) return '';  
  const pad = (n)=>String(n).padStart(2,'0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// 候補キーから最初に見つかった日時値を返す（reservedAt / createdAt / lastNotifyAt など）
const pickFirstDateLike = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (!v) continue;
    if (v?.toDate || v instanceof Date || typeof v === 'number') return v;
  }
  return null;
};



export default function QueueAdmin() {
  // —— 左サイド（管理トグル相当）——
  const [inhouseOn, setInhouseOn] = useState(false); // 院内受付
  const [onlineOn, setOnlineOn]   = useState(false); // ネット受付

  // 二度押し防止 & チラつき抑制用
  const [busyAdd, setBusyAdd] = useState(false);
  const [busyInhouse, setBusyInhouse] = useState(false);
  const [busyOnline, setBusyOnline]   = useState(false);
  const [busyRecallMap, setBusyRecallMap] = useState(() => new Map());

  // サーバ反映待ちの“意図した状態”を保持（null なら未ペンディング）
  const [pendingInhouse, setPendingInhouse] = useState(null); // null | boolean
  const [pendingOnline, setPendingOnline]   = useState(null); // null | boolean


  const [showDeleted, setShowDeleted] = useState(false); // 「開始済・削除データ」表示
  const [clock, setClock] = useState(fmtTime());

  /** 予約番号リスト配列 @type {Array<{id:string,no:number,createdAt?:string,lineAt?:string,status:'waiting'|'serving'|'hold'|'done'|'canceled'}>} */
  const [rows, setRows] = useState([]);
  const [slotsErr, setSlotsErr] = useState(null);
  
  const [nextNo, setNextNo] = useState(null);
  const [nextErr, setNextErr] = useState(null);


  // 5秒点滅の管理
  const [blinkingIds, setBlinkingIds] = useState(new Set()); // Set<string>
  const blinkTimersRef = useRef(new Map()); // Map<string, number>
  const prevCanceledRef = useRef(new Map()); // Map<string, boolean>
  const firstLoadRef = useRef(true); // 初回スナップショットは通知しない// 予約の立ち上がり検出用（前回の“LINE/PWA予約中”かどうか）
  const prevReservedLineRef = useRef(new Map()); // Map<slotId, boolean>
  const prevReservedAtRef = useRef(new Map());   // Map<slotId, number|null>

  const tsToMillis = (v) => {
    if (!v) return null;
    if (typeof v?.toMillis === 'function') return v.toMillis();
    const d = v?.toDate?.() ?? (v instanceof Date ? v : new Date(v));
    const ms = d?.getTime?.();
    return Number.isFinite(ms) ? ms : null;
  };

  // 通知（LINE/PWAから予約が入った）
  const [arrivalNotice, setArrivalNotice] = useState({ open:false, channel:null }); // {open:boolean, channel:'LINE'|'PWA'|null}

  // 予約検出時に音を鳴らす
  const { playBeep, soundOn, toggleSound, soundLocked, unlockSound } = useBeep();

  
  // 時計
  useEffect(() => {
    const t = setInterval(()=>setClock(fmtTime()), 1000);
    return () => clearInterval(t);
  }, []);

  // 受付状態ドキュメントを常時監視
  useEffect(() => {
    const ref = doc(db, 'admin', 'runtime'); // ← 単一ドキュメント
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.data() || {};
      setInhouseOn(!!d.inhouseOn);
      setOnlineOn(!!d.onlineOn);
    }, (err) => {
      console.error('reception onSnapshot error', err);
    });
    return () => unsub();
  }, [db]);

  // サーバー側の反映を見て pending を解除
  useEffect(() => {
    if (pendingInhouse != null && inhouseOn === pendingInhouse) setPendingInhouse(null);
  }, [inhouseOn, pendingInhouse]);

  useEffect(() => {
    if (pendingOnline != null && onlineOn === pendingOnline) setPendingOnline(null);
  }, [onlineOn, pendingOnline]);

  // 予約枠
  // （予約済みのみ & reservationNumber 昇順）→ rows へマッピング
  // （院内受付ONのときだけ購読）
  useEffect(() => {
    setSlotsErr(null);
    // OFFになったら購読解除＆表示リセット
    console.log('[effect] inhouseOn =', inhouseOn);
    if (!inhouseOn) {
      setRows([]); // ← OFF時も直前データを残したいなら、この行を消してください
      return;
    }

    // 再購読のたびに初回フラグを立て直す（初回は通知しない）
    firstLoadRef.current = true;

    // ★ ここで前回状態をリセット
    prevReservedLineRef.current.clear();

    // ONになったら購読開始
    const colRef = collection(db, 'reservation_slots');

    const q = showDeleted
                        ? query(
                            colRef,
                            where('isReserved', '==', true),
                            orderBy('reservationNumber', 'asc'),
                            limit(500)
                          )
                        : query(
                            colRef,
                            where('isReserved', '==', true),
                            where('isReceptionAccepted', '==', false),
                            where('canceled', '==', false),
                            orderBy('reservationNumber', 'asc'),
                            limit(500)
                          );


    const unsub = onSnapshot(
      q,
      (snap) => {
        const mapped = snap.docs.map((docSnap) => {
          const d = docSnap.data({ serverTimestamps: 'estimate' });
          const noNum = Number(d?.reservationNumber ?? 0);
          // const createdLike = pickFirstDateLike(d, ['reservedAt']);
          // reservedAt が無い古いデータでも表示できるようにフォールバック
          const createdLike = pickFirstDateLike(d, ['reservedAt', 'createdAt', 'updatedAt']);

          // const lineLike    = pickFirstDateLike(d, ['notifiedAheadAt','lastNotifyAt','lineAt','notifiedAt']);
          const recallLike  = pickFirstDateLike(d, ['notifiedAheadAt']);// 再呼出（notifiedAheadAt）
          const recallAtHM  = toHM(recallLike);
          const ahead5Like  = pickFirstDateLike(d, ['notifiedAt']);// 5人前通知（notifiedAt）
          const ahead5AtHM  = toHM(ahead5Like);

          const ahead5SkippedReason = d?.ahead5SkippedReason || null;
          const ahead5SkippedAtLike = pickFirstDateLike(d, ['ahead5SkippedAt']);
          const ahead5SkippedAtHM   = toHM(ahead5SkippedAtLike);

          const acceptedLike = pickFirstDateLike(d, ['receptionAcceptedAt']);
          const acceptedAtHM = toHM(acceptedLike);
          
          const canceledLike   = pickFirstDateLike(d, ['canceledAt']);// ★★ 管理者が削除(canceledAt)
          const canceledAtHM   = toHM(canceledLike);
          //保留
          const holdLike     = pickFirstDateLike(d, ['holdAt']);
          const holdAtHM     = toHM(holdLike);

          const status =
            d?.canceled === true ? 'canceled' :
            d?.isReceptionAccepted === true ? 'serving' :
            (d?.holdAt ? 'hold' : 'waiting');

          const reservedType = d?.reservedType || null; // "LINE"|"PWA"|"ADMIN"|null
          const lineCanceled = !!d?.canceledLineAt;     // ★患者キャンセル表示（LINE）
          const pwaCanceled = !!d?.canceledPwaAt;     // ★患者キャンセル表示（PWA）

          const adminConfirmed = !!d?.adminConfirmedAt;                 // ← 追加
          const needsConfirm   = (d?.reservedType === 'LINE' || d?.reservedType === 'PWA') && !adminConfirmed;  // ← 追加


          return {
            id: docSnap.id,
            no: Number.isFinite(noNum) ? noNum : 0,
            createdAt: toHM(createdLike),       // 受付時刻（reservedAt）→ HH:MM
            recallAt: recallAtHM,               // 再呼出時刻（notifiedAheadAt）→ HH:MM
            ahead5At: ahead5AtHM,               // 5人前通知（notifiedAt）→ HH:MM
            ahead5SkippedReason,                 // "adminConfirmed" 等
            ahead5SkippedAt: ahead5SkippedAtHM,  // 任意で使える（表示はしなくてもOK）
            status,
            reservedType,
            lineCanceled,
            pwaCanceled,
            acceptedAt: acceptedAtHM,           // 診察開始時刻 → HH:MM
            canceledAt: canceledAtHM,           // 削除時刻（HH:MM）
            holdAt: holdAtHM,                   // 保留時刻（HH:MM）
            adminConfirmed,                     // 管理者LINE・PWA予約確認
            needsConfirm,                       // 管理画面サイドバー LINE・PWA予約 点滅表示必要可否フラグ
          };
        });
        mapped.sort((a,b) => a.no - b.no);
        setRows(mapped);

        if (firstLoadRef.current) {
          // …setRows(mapped) の後あたりで
          snap.docs.forEach(ds => {
            const d = ds.data();

            const isReservedLine =
              d?.isReserved === true &&
              d?.canceled !== true &&
              (d?.reservedType === 'LINE' || d?.reservedType === 'PWA');
              prevReservedAtRef.current.set(ds.id, tsToMillis(d?.reservedAt)); 



            // （任意）キャンセル検出用の初期化も入れるなら：
            const nowCanceled = !!d?.canceledLineAt;
            prevCanceledRef.current.set(ds.id, nowCanceled);

            const nowCanceledPwa = !!d?.canceledPwaAt;
            prevCanceledRef.current.set(ds.id, nowCanceledPwa);
          });
          firstLoadRef.current = false;
        } else {
          // 変更検出（↓3を適用）
        }

        // 1) 初回スナップショットは通知しない
        if (firstLoadRef.current) {
          // 初回ロード分の canceled 状態を prev に反映（上でset済み）
          firstLoadRef.current = false;
        } else {
          // 2) docChanges から新規予約（LINE/PWA）を検出
          const changes = snap.docChanges();

          // ★ ここで removed を先に掃除
          // for (const ch of changes) {
          //   if (ch.type === 'removed') {
          //     prevReservedLineRef.current.delete(ch.doc.id);
          //   }
          // }

          // ★ removed は「今は予約表示中ではない」とだけ記録（reservedAt は保持）
          for (const ch of changes) {
            if (ch.type === 'removed') {
              prevReservedLineRef.current.set(ch.doc.id, false);
            }
          }

          for (const ch of changes) {
            const id = ch.doc.id;
            const d  = ch.doc.data();

            // 現在の “LINE/PWAで予約中” 判定
            const isReservedLine =
              d?.isReserved === true &&
              d?.canceled !== true &&
              (d?.reservedType === 'LINE' || d?.reservedType === 'PWA');

            // 前回値（未登録なら false）
            const wasReservedLine = !!prevReservedLineRef.current.get(id);
            const reservedKeyNow  = tsToMillis(d?.reservedAt);
            const reservedKeyPrev = prevReservedAtRef.current.get(id) ?? null;


            // ★ 候補：LINE予約化の立ち上がり かつ まだ adminBeepAnnounced が無い
            //    さらに reservedAt が新規/変化時のみ（無駄Tx抑制）
            if (!wasReservedLine && isReservedLine && !d?.adminBeepAnnounced) {
              if (reservedKeyNow == null || reservedKeyNow !== reservedKeyPrev) {
                // Txで「未通知なら刻む」→ 刻めた時だけビープ
                tryBeepAnnounceTx(id);
              }
            }


            // 前回値を更新
            prevReservedLineRef.current.set(id, isReservedLine);

            // （既存の“患者キャンセル検出”ロジックはそのまま）
          }

          // 3) 患者キャンセルの発火（canceledLineAt: null -> 非null）
          for (const ch of snap.docChanges()) {
            if (ch.type !== 'added' && ch.type !== 'modified') continue;
            const id = ch.doc.id;
            const d = ch.doc.data();
            const prev = prevCanceledRef.current.get(id) || false;
            const nowCanceled = !!d?.canceledLineAt;
            const nowCanceledPwa = !!d?.canceledPwaAt;
            if (!prev && nowCanceled) {
              // 5秒だけ点滅
              setBlinkingIds(prevSet => {
                const ns = new Set(prevSet);
                ns.add(id);
                return ns;
              });
              // 5秒後に点滅解除（背景ピンクは維持）
              const t = setTimeout(() => {
                setBlinkingIds(prevSet => {
                  const ns = new Set(prevSet);
                  ns.delete(id);
                  return ns;
                });
                blinkTimersRef.current.delete(id);
              }, 5000);
              // 既存タイマーがあれば張り替え
              if (blinkTimersRef.current.get(id)) {
                clearTimeout(blinkTimersRef.current.get(id));
              }
              blinkTimersRef.current.set(id, t);
            }
            // 状態を更新
            prevCanceledRef.current.set(id, nowCanceled);
            prevCanceledRef.current.set(id, nowCanceledPwa);
          }
        }









      },
      (err) => {
        console.error('reservation_slots onSnapshot error', err);
        setSlotsErr(err);
      }
    );
    return () => {

      // タイマー掃除
      for (const t of blinkTimersRef.current.values()) clearTimeout(t);
      blinkTimersRef.current.clear();

      // ビープ用の前回状態をクリア
      prevReservedLineRef.current.clear();
      //ビープ音のクリア
      prevReservedAtRef.current.clear(); 
      // キャンセル検出もリセットしたいなら
      prevCanceledRef.current.clear();

      console.log('[cleanup] unsubscribe');
      unsub();// ← inhouseOn が false になったりアンマウント時に解放
    };
}, [inhouseOn, showDeleted, db]);


 // 予約枠（院内受付ONのときだけ購読）…既存の rows 用 useEffect はそのまま
 // 追加：次の予約番号（isReserved==false の最小 reservationNumber）を購読
 useEffect(() => {
  // 既存の rows 用監視とは独立して動かす
   setNextErr(null);
   if (!inhouseOn) {
     setNextNo(null);
     return;
   }
   const colRef = collection(db, 'reservation_slots');
   const q = query(
     colRef,
     where('isReserved', '==', false),
     orderBy('reservationNumber', 'asc'),
     limit(1)
   );
   const unsub = onSnapshot(
     q,
     (snap) => {
       if (snap.empty) {
         setNextNo(null); // 空きがない
       } else {
         const d = snap.docs[0].data();
         setNextNo(Number(d?.reservationNumber ?? null));
       }
     },
     (err) => {
       console.error('nextNo onSnapshot error', err);
       setNextErr(err);
     }
   );
   return () => unsub();
 }, [inhouseOn, db]);


  // 最大番号
  const maxNo = useMemo(() => rows.reduce((m,r)=>Math.max(m, r.no), 0), [rows]);


  // 表示対象（「開始済・削除データ」チェックに連動）
  const visibleRows = useMemo(() => {
    if (showDeleted) return rows;
    return rows.filter(r => !['done','canceled','serving'].includes(r.status)); // 例：待ち/保留だけ表示
  }, [rows, showDeleted]);

  // 待機中（waiting かつ lineCanceled またはpwaCanceledでない）だけを予約番号順に並べて 1,2,3…を付与
  const waitingPosMap = useMemo(() => {
    const waiting = [...rows]
      .filter(r => r.status === 'waiting' && (!r.lineCanceled&&!r.pwaCanceled))
      .sort((a,b) => a.no - b.no);
    const m = new Map();
    let i = 1;
    for (const r of waiting) m.set(r.id, i++);
    return m; // Map<string, number>
  }, [rows]);

  // adminBeepAnnounced を「未設定なら付与」→ 付与できた時だけビープ
  const tryBeepAnnounceTx = async (slotId) => {
    const ref = doc(db, 'reservation_slots', slotId);
    try {
      const didMark = await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) return false;
        const d = snap.data();
        const isLineReserved = d?.isReserved === true && d?.canceled !== true && d?.reservedType === 'LINE';
        if (!isLineReserved) return false;
        if (d?.adminBeepAnnounced) return false; // すでに通知済み
        tx.update(ref, { adminBeepAnnounced: serverTimestamp(), updatedAt: serverTimestamp() });
        return true;
      });
      if (didMark) {
        setArrivalNotice({ open: true, channel: 'LINE' });
        playBeep();
      }
    } catch (e) {
      console.error('tryBeepAnnounceTx failed', e);
    }
  };


  // 院内受付トグル（inhouseOn の状態に応じて Start/End を呼ぶ）
  // 院内受付トグル（開始/終了）
  const handleInhouseClick = async () => {
    if (busyInhouse || busyOnline) return; // どちらか処理中はブロック
    const next = !inhouseOn;

    const ok = window.confirm(
      next
        ? '院内受付を開始します。\n・reservation_slotsを初期化して200件を生成します。\nよろしいですか？'
        : '院内受付を終了します。\n（予約がある場合、ネット受付が開始中の場合は終了できません）\nよろしいですか？'
    );
    if (!ok) return;

    setBusyInhouse(true);
    setPendingInhouse(next);  // 表示は pending を優先して安定化
    try {
      if (next) {
        await startInhouseReception();   // バックアップ＆生成
      } else {
        await endInhouseReception();     // 未受付残・ネットONはCF側で弾く
      }
      // 反映は onSnapshot を待ち、そこで pending を解除
    } catch (e) {
      console.error(e);
      alert(e?.message || String(e));
      setPendingInhouse(null); // 失敗したので pending 解除
    } finally {
      setBusyInhouse(false);
    }
  };

  // ネット受付トグル（開始/終了）
  const handleOnlineClick = async () => {
    if (busyInhouse || busyOnline) return;
    const next = !onlineOn;

    const ok = window.confirm(
      next
        ? 'ネット受付を開始します。よろしいですか？\n（院内受付が停止中の場合は開始できません）'
        : 'ネット受付を停止します。よろしいですか？'
    );
    if (!ok) return;

    setBusyOnline(true);
    setPendingOnline(next);
    try {
      if (next) {
        await startOnlineReception();   // 院内OFFならCF側で弾く
      } else {
        await endOnlineReception();
      }
    } catch (e) {
      console.error(e);
      alert(e?.message || String(e));
      setPendingOnline(null);
    } finally {
      setBusyOnline(false);
    }
  };


  // 追加（新規受付）: 最小の空き番号を確保（isReserved=true, reservedAt をセット）
  const addNew = async () => {
    if (busyAdd) return;

    if (!dispInhouse) {
      alert('院内受付が停止中です。先に「院内受付」を開始してください。');
      return;
    }
    
    setBusyAdd(true);
    try {
      await allocateNextSlot({ reservedType: "ADMIN" });
      // 反映は onSnapshot が拾うのでローカル追加は不要
    } catch (e) {
      // CF 側でもガードしているので、ここでコード別に文言出し分け
      const msg = e?.message || "予約番号の確保に失敗しました。";
      if (e?.code === 'failed-precondition') {
        alert('院内受付が停止中です。先に「院内受付」を開始してください。');
      } else {
        alert(msg);
      }
    } finally {
      setBusyAdd(false);
    }
  };

  const slotRef = (id) => doc(db, 'reservation_slots', id);
  const doStart   = async (id) => {
    await updateDoc(slotRef(id), {
      isReceptionAccepted: true,
      receptionAcceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const confirmOneReservation = async (id) => {
  try {
    await updateDoc(doc(db, 'reservation_slots', id), {
      adminConfirmedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    // onSnapshot が反映を拾うので setState は不要
  } catch (e) {
    console.error(e);
    alert(e?.message || '予約の確認フラグ更新に失敗しました。');
  }
};


  const doRecall = async (id, no) => {
    // 二度押し防止
    if (busyRecallMap.get(id)) return;
    // 確認ダイアログ
    const ok = window.confirm(
      no
        ? `受付番号「${no}」に再呼出メッセージ（LINE）を送信します。よろしいですか？`
        : '再呼出メッセージ（LINE）を送信します。よろしいですか？'
    );
    if (!ok) return;
    setBusyRecallMap(prev => new Map(prev).set(id, true));

    try {
      await sendReservationMessageBySlot({ slotId: id, kind: "recall" });
      // 成功すると Functions 側で notifiedAheadAt を更新 → onSnapshot が拾う
    } catch (e) {
      console.error(e);
      alert(e?.message || "再呼出に失敗しました（送信エラー）");
    } finally {
      setBusyRecallMap(prev => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const doCancel  = async (id) => {
    await updateDoc(slotRef(id), {
      canceled: true,                      // 手動削除（通常非表示）
      canceledAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };
  const doHold    = async (id) => {
    await updateDoc(slotRef(id), {
      hold:true,
      holdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };
  const undoStart = async (id) => {
    await updateDoc(slotRef(id), {
      isReceptionAccepted: false,
      receptionAcceptedAt: deleteField(),
      updatedAt: serverTimestamp(),
    });
  };
  const undoCancel = async (id) => {
    console.log("undoCancel id",id);
    await updateDoc(slotRef(id), {
      canceled: false,       
      canceledAt: null,     
      updatedAt: serverTimestamp(),
    });
  };
  const undoHold = async (id) => {
    await updateDoc(slotRef(id), {
      hold:false,
      holdAt: deleteField(),
      updatedAt: serverTimestamp(),
    });
  };


  // 画面用の表示状態：pending があればそれを優先
  const dispInhouse = (pendingInhouse ?? inhouseOn);
  const dispOnline  = (pendingOnline  ?? onlineOn);

return (
    <div id="top-panel">
      <div className="wholebody">
        <div className="outer-top">
          {/* 固定サイドバー */}
          <div id="booklistsidebar">
            <div className="sidebar-area">
              <div id="newWaitButton"
                  className="btn-flat-border-menu"
                  onClick={addNew}
                  aria-busy={busyAdd}  
                  aria-disabled={!dispInhouse}    
                  title={dispInhouse ? '新規受付' : '院内受付を開始してください'}
                  style={{ cursor: busyAdd ? 'wait' : 'pointer', opacity: busyAdd ? 0.7 : 1 }}
              ><span className="btn-label">新規受付</span></div>


              <div id="maxWaitNo" className="max-wait-css">
                {nextNo == null ? '—' : `${nextNo}番`}
                {nextErr && <div style={{color:'tomato', fontSize:12}}>番号取得エラー</div>}
              </div>


              <div className="current-clock-whole">
                <div className="current-clock-whole-up">
                  <div id="RealtimeClockArea" className="current-clock-time">{clock}</div>
                </div>
                <div className="current-clock-whole-up">

                  <div
                    id="RealtimeCheckHospital"
                    className={dispInhouse ? 'current-online-hospital' : 'current-offline-hospital'}
                    onClick={handleInhouseClick}
                    style={{
                      cursor: (busyInhouse || busyOnline) ? 'not-allowed' : 'pointer',
                      opacity: (pendingInhouse != null || busyInhouse) ? 0.8 : 1
                    }}
                  >
                    {(pendingInhouse != null || busyInhouse)? <>切替中…<i className="fas fa-spinner fa-fw fa-spin"/></>
                      : (dispInhouse ? <>院内受付中<i class='fas fa-dot-circle fa-fw'></i></> : <>院内停止中<i class='fas fa-stop-circle fa-fw'></i></>)
                    }
                  </div>
                  <div
                    id="RealtimeCheckOnline"
                    className={dispOnline ? 'current-online' : 'current-offline'}
                    onClick={handleOnlineClick}
                    style={{
                      cursor: (busyInhouse || busyOnline) ? 'not-allowed' : 'pointer',
                      opacity: (pendingOnline != null || busyOnline) ? 0.8 : 1
                    }}
                  > 
                    {(pendingOnline != null || busyOnline) ? <>切替中…<i className="fas fa-spinner fa-fw fa-spin"/></>
                          : (dispOnline
                              ? <>ネット受付中<i class='fas fa-dot-circle fa-fw'></i></>
                              : <>ネット停止中<i class='fas fa-stop-circle fa-fw'></i></>
                            )
                      }
                  </div>
                </div>
              </div>
              <div className="mobile-controls-row">
                <div className="current-delete">
                  <div className="current-delete-title">開始済・削除データ</div>
                  <div className="round">
                    <input
                      type="checkbox"
                      id="checkbox"
                      checked={showDeleted}
                      onChange={e=>setShowDeleted(e.target.checked)}
                    />
                    <label htmlFor="checkbox"></label>
                  </div>
                </div>           
                <div className="current-delete">
                  <div className="current-delete-title">通知音</div>
                  <div className="round" title="新規予約が来たらビープ音を鳴らす">
                    <input
                      type="checkbox"
                      id="soundCheckbox"
                      checked={soundOn}
                      onChange={e => toggleSound(e.target.checked)}
                    />
                    <label htmlFor="soundCheckbox"></label>
                  </div>

                  {soundLocked && (
                    <button
                      type="button"
                      className="list-button"
                      style={{ marginLeft: 8 }}
                      onClick={unlockSound}
                      title="クリックして音声を有効化（ブラウザの自動再生対策）"
                    >
                      🔔 音を有効化
                    </button>
                  )}
                </div>  


                <div className="current-delete">
                  {/* 🔊 Beepテストボタン */}
                  <button
                    type="button"
                    className="list-button"
                    onClick={async () => {
                              console.log('TEST CLICK');
                              await unlockSound();
                            }}
                    title="ビープ音テスト"
                  >
                    🔊 テスト
                  </button>
                </div>  
              </div>
              

              {/* ローディング枠（デザインのみ） */}
              <div id="loader-whole-bg" className="display-none">
                <div className="loading-flexbox-whole">
                  <div className="load-animate-fa-whole gray-color">
                    <i className="fas fa-spinner fa-5x fa-spin"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 本体右側 */}
          <div className="body">
            {/* ヘッダ行（添付HTMLのまま） */}
            <div className="list-header">
              <div className="list-header-cientid tooltip3">
                <p>受付番号<i className="fas fa-question-circle fa-fw font-smaller"></i></p>
                <div className="description3">診察開始時に当該ボタンまたは”開始”ボタンをクリック。</div>
              </div>
              <div className="list-header-waitcnt tooltip3">
                <p>待ち人数<i className="fas fa-question-circle fa-fw font-smaller"></i></p>
                <div className="description3">診察開始まで待っている人数</div>
              </div>
              <div className="list-header-time tooltip3">
                <p>受付時刻<i className="fas fa-question-circle fa-fw font-smaller"></i></p>
                <div className="description3">待ち順番システムに患者さんが予約を入れた日時</div>
              </div>
              <div className="list-header-time tooltip3">
                <p>5人前通知<i className="fas fa-question-circle fa-fw font-smaller"></i></p>
                <div className="description3">自動送信の“来院依頼”メッセージの送信日時</div>
              </div>
              <div className="list-actions-header">
                <div className="list-header-button tooltip3">
                  <p>診察開始<i className="fas fa-question-circle fa-fw font-smaller"></i></p>
                  <div className="description3">
                    診察開始時に”開始”ボタンをクリック。<br/>
                    <font color="orange">「開始済・削除データ」表示中は</font>
                    <font color="red">×印</font>
                    <font color="orange">で取り消し可能</font>
                  </div>
                </div>
                <div className="list-header-button tooltip3">
                  <p>再呼出<i className="fas fa-question-circle fa-fw font-smaller"></i></p>
                  <div className="description3">来院されない場合にクリック。LINEで催促</div>
                </div>
                <div className="list-header-button tooltip3">
                  <p>削除<i className="fas fa-question-circle fa-fw font-smaller"></i></p>
                  <div className="description3">予約キャンセル時にクリック</div>
                </div>
                <div className="list-header-button tooltip3">
                  <p>保留<i className="fas fa-question-circle fa-fw font-smaller"></i></p>
                  <div className="description3">来院されない場合に一時保留</div>
                </div>
              </div>
            </div>

            {/* リスト本体 */}
            <div className="list-body-zentai">
              {/* {visibleRows.map((r) => { */}

              {visibleRows.map((r, idx) => {
                const isBlinking = blinkingIds.has(r.id);
                const isUnconfirmedLine = r.reservedType === 'LINE' && r.needsConfirm && !r.lineCanceled && r.status !== 'canceled';
                const isUnconfirmedPwa = r.reservedType === 'PWA' && r.needsConfirm && !r.pwaCanceled && r.status !== 'canceled';

                const rowCls =
                  r.status === 'canceled'           ? 'display-pink' :
                  r.lineCanceled === true           ? `display-pink ${isBlinking ? 'blink' : ''}` :
                  r.pwaCanceled === true           ? `display-pink ${isBlinking ? 'blink' : ''}` :
                  r.status === 'hold'               ? 'display-blue' :
                  isUnconfirmedLine                 ? 'display-unconfirmed' :
                  isUnconfirmedPwa                 ? 'display-unconfirmed' :
                  '';

                const recallBusy = !!busyRecallMap.get(r.id);
                const recallable = (r.reservedType === 'LINE' && !r.recallAt)||(r.reservedType === 'PWA' && !r.recallAt);// LINE 予約かつ “未送信（notifiedAheadAt が未設定）” の時だけ再呼出可能


                return (
                  <div key={r.id} className={`list-body ${rowCls}`}>
                    <div className="list-body-cientid">
                       {/* <div className={noClass}>{r.no}</div> */}
                       {(() => {
                          // 「番号クリックで開始」できるか？（開始済/キャンセルは不可）
                          const canStart = !(r.status === 'serving' || r.status === 'canceled' || r.lineCanceled || r.pwaCanceled);

                          // 見た目：診察中だけ緑バッジ、それ以外は“文字のみ”
                          const noClass = (r.status === 'serving'|| r.status === 'canceled' || r.lineCanceled || r.pwaCanceled) ? 'list-text' : 'list-button-green';

                          return (
                            <div
                              className={noClass}
                              role={canStart ? 'button' : undefined}
                              onClick={canStart ? () => doStart(r.id) : undefined}
                              title={canStart ? 'クリックで開始' : undefined}
                              style={canStart ? { cursor: 'pointer', userSelect: 'none' } : { userSelect: 'none' }}
                            >
                              {r.no}
                            </div>
                          );
                        })()}
                    </div>
                    {/* 待ち人数（waiting & 非lineCanceledのみ 1,2,3,…） */}
                    <div className="list-body-waitcnt">
                      {/* <div className="list-text">{idx + 1}人</div> */}
                        <div className="list-text">
                          {waitingPosMap.get(r.id) != null ? `${waitingPosMap.get(r.id)}人` : '—'}
                        </div>
                    </div>
                    {/* <div className="list-body-time">
                      <div className="list-text time-cell"> */}
                    <div className="list-body-time">
                      {(() => {
                        const confirmable =
                          (r.reservedType === 'LINE' || r.reservedType === 'PWA') &&
                          r.needsConfirm === true &&
                          (!r.lineCanceled||!r.pwaCanceled )&&
                          r.status !== 'canceled';
                        return (
                          <div
                            className="list-text time-cell"
                            onClick={confirmable ? () => confirmOneReservation(r.id) : undefined}
                            title={confirmable ? 'クリックで確認済みにする（アイコンがグレーになります）' : undefined}
                            style={confirmable ? { cursor: 'pointer', userSelect: 'none' } : { userSelect: 'none' }}
                          >

                        {r.lineCanceled ? (<><i id='delete-time-i' className='fas fa-heart-broken fa-fw faa-ring animated'></i></>) : ''}
                        {r.pwaCanceled ? (<><i id='delete-time-i' className='fas fa-heart-broken fa-fw faa-ring animated'></i></>) : ''}

                        {r.reservedType === 'LINE' && (
                          <i
                            className={`fab fa-line fa-fw ${(r.needsConfirm === true) ? 'color-green faa-ring animated' : 'color-gray'}`}
                            aria-label="LINE"
                          />
                        )}
                        {r.reservedType === 'PWA' && (
                          <i
                            className={`fas fa-mobile-alt fa-fw ${(r.needsConfirm === true) ? 'color-green faa-ring animated' : 'color-gray'}`}
                            aria-label="PWA"
                          />
                        )}

                        {r.createdAt || '—'}
                      {/* </div>
                    </div> */}
                      </div>
                        );
                      })()}
                    </div>

                    <div className="list-body-time">
                      <div className="list-text">
                        {r.ahead5SkippedReason === 'adminConfirmed' ? (
                          /* 来院済のため未送信（薄い青） */
                          <div className="badge-ahead5 badge-ahead5--skip" title="来院確認済みのため5人前は送信していません">
                            <div className="label">来院未送信</div>
                            <div className="time-skip">{r.ahead5SkippedAt || '—'}</div>
                          </div>
                        ) : r.ahead5At ? (
                          /* 送信済（薄い黄色） */
                          <div className="badge-ahead5 badge-ahead5--sent" title="5人前通知を送信しました">
                            <div className="label">送　信　済</div>
                            <div className="time">{r.ahead5At}</div>
                          </div>
                        ) : (
                          '—'
                        )}
                      </div>
                    </div>


                    <div className="list-actions">


                      {/* 診察開始 */}
                      <div className="list-body-button">
                        {r.status === 'serving' ? (
                          // ★ 開始済み：時刻＋× を表示。クリックで取り消し
                          <div
                            className="list-text list-button-label start-time"
                            title="クリックで開始を取り消す"
                            onClick={() => undoStart(r.id)}
                            role="button"
                          >
                            <span className="start-time-text">{r.acceptedAt || '—'}</span>
                            <i className="fas fa-times-circle fa-fw icon-red" aria-hidden="true" />
                          </div>
                        ) : (r.lineCanceled || r.pwaCanceled || r.status === 'canceled') ? (
                          // 患者キャンセルは非表示（幅は保持）
                          <div className="list-button placeholder" aria-hidden="true">開始</div>
                        ) : (
                          // 未開始：通常の開始ボタン
                          <div className="list-button" onClick={() => doStart(r.id)}>
                            開始<i class="fa fa-plus-square" aria-hidden="true"></i>
                          </div>
                        )}
                      </div>



                      <div className="list-body-button">
                        {r.recallAt ? (
                          // 送信済み：送信時刻をラベル表示（クリック不可）
                          <div className="list-text list-button-label recall-time" title="再呼出済み">
                            <span className="recall-time-text">{r.recallAt}</span>
                          </div>
                        ) : (r.lineCanceled ||r.pwaCanceled || r.status === 'canceled' || !recallable) ? (
                          // 操作不可（幅は保持）
                          <div className="list-button placeholder" aria-hidden="true">再呼出</div>
                        ) : (
                          // 送信可能：再呼出ボタン
                          <div
                            className="list-button"
                            onClick={recallBusy ? undefined : () => doRecall(r.id, r.no)}
                            style={{ cursor: recallBusy ? 'wait' : 'pointer', opacity: recallBusy ? 0.6 : 1 }}
                            aria-disabled={recallBusy}
                          >
                            {recallBusy ? <>送信中…<i className="fas fa-spinner fa-fw fa-spin"/></> : "再呼出"}
                          </div>
                        )}
                      </div>


                      {/* 削除 */}
                      <div className="list-body-button">
                      {r.status === 'canceled'
                        ? (
                          // 手動削除済み：時刻＋×（クリックで取り消し）
                          <div
                            className="list-text list-button-label cancel-time"
                            title="クリックで削除を取り消す"
                            onClick={() => undoCancel(r.id)}
                            role="button"
                          >
                            <span className="cancel-time-text">{r.canceledAt || '—'}</span>
                            <i className="fas fa-times-circle fa-fw icon-red" aria-hidden="true" />
                          </div>
                        )
                        : (
                          // それ以外（lineCanceledとpwaCanceled を含む）：通常の削除ボタンを表示
                          <div className="list-button" onClick={()=>doCancel(r.id)}>
                            削除<i id="deleteButton-i" className="fas fa-times-circle"></i>
                          </div>
                        )
                      }
                      </div>

                      {/* 保留 */}
                      <div className="list-body-button">

                        {r.status === 'hold' ? (
                            // ★ 保留中：保留時刻＋×。クリックで保留解除
                            <div
                              className="list-text list-button-label hold-time"
                              title="クリックで保留を解除"
                              onClick={() => undoHold(r.id)}
                              role="button"
                            >
                              <span className="hold-time-text">{r.holdAt || '—'}</span>
                              <i className="fas fa-times-circle fa-fw icon-red" aria-hidden="true" />
                            </div>
                          ) : (r.lineCanceled ||r.pwaCanceled || r.status === 'canceled') ? (
                            // 患者キャンセル / 手動削除行は操作不可（幅は保持）
                            <div className="list-button placeholder" aria-hidden="true">保留</div>
                          ) : (
                            // 通常：保留ボタン
                            <div className="list-button" onClick={()=>doHold(r.id)}>
                              保留<i id='onHoldButton-i' className='fas fa-minus-circle'></i>
                            </div>
                        )}

                      </div>

                    </div>

                  </div>
                );
              })}
              
            </div>
          </div>
          {/* end body */}
        </div>
      </div>
    </div>
  );
}
